import { getIdToken } from "../../auth";
import type { ExtractedDraft } from "../schema";

function apiBase(): string {
  const url = import.meta.env.VITE_API_FUNCTION_URL as string | undefined;
  if (!url) throw new Error("VITE_API_FUNCTION_URL is not set.");
  return url.replace(/\/$/, "");
}

async function authHeader(): Promise<HeadersInit> {
  const token = await getIdToken();
  return { Authorization: `Bearer ${token}` };
}

async function check(r: Response): Promise<Response> {
  if (!r.ok) {
    // Surface the server's human message (e.g. "PDF has 32 pages; limit is 30.")
    // rather than a raw "API 400: {…}" blob.
    let msg = r.statusText;
    try {
      const body = await r.json();
      msg = body?.error ?? JSON.stringify(body);
    } catch {
      msg = (await r.text().catch(() => r.statusText)).slice(0, 200);
    }
    throw new Error(msg);
  }
  return r;
}

export interface ExtractResponse {
  drafts: Record<string, ExtractedDraft>;
  /** How many fields the doc covered that were already answered (left untouched). */
  skippedAnswered?: number;
}

export async function extractDoc(
  sessionId: string,
  file: File,
): Promise<ExtractResponse> {
  const form = new FormData();
  form.append("file", file);
  const r = await check(
    await fetch(`${apiBase()}/canvas/${sessionId}/extract`, {
      method: "POST",
      headers: await authHeader(),
      body: form,
    }),
  );
  return (await r.json()) as ExtractResponse;
}

export async function discardDraft(
  sessionId: string,
  fieldName: string,
): Promise<void> {
  await check(
    await fetch(
      `${apiBase()}/canvas/${sessionId}/draft/${fieldName}/discard`,
      {
        method: "POST",
        headers: await authHeader(),
      },
    ),
  );
}
